/**
 * Approval Queue Dashboard Component
 *
 * FERP CRITICAL: Template approval queue management for life-critical AI templates
 * Dual-control approval workflow with criticality-based routing
 *
 * Principles:
 * - All approval data from backend (GraphQL query)
 * - Criticality-based prioritization (life-critical → critical → operational)
 * - Deadline tracking with automatic escalation
 * - Repository pattern (no direct API calls)
 * - Mantine UI components only
 * - Real-time polling for queue updates
 *
 * Regulatory Requirement: FDA/HIPAA dual-control approval for life-critical AI templates
 */

import React, { useState, useMemo } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Select,
  Progress,
  Avatar,
  Tooltip,
  Title,
  Alert,
  ThemeIcon,
  Paper,
  Loader,
  ActionIcon,
  TextInput,
} from '@mantine/core';
import {
  IconClock,
  IconAlertTriangle,
  IconCheck,
  IconRefresh,
  IconShieldCheck,
  IconUser,
  IconSearch,
  IconFilter,
} from '@tabler/icons-react';
import {
  useApprovalQueue,
  type ApprovalWorkflowInstance,
  type ApprovalQueueFilter,
} from '../hooks/useApprovalQueue';
import { useCurrentTenant } from '../hooks/useCurrentTenant';

interface ApprovalQueueDashboardProps {
  onReviewTemplate?: (instanceId: string) => void;
  compact?: boolean;
}

/**
 * Approval Queue Dashboard
 *
 * Displays pending approval templates with:
 * - Criticality-based prioritization
 * - Deadline countdowns
 * - Dual-control progress tracking
 * - Approver assignment visualization
 */
export function ApprovalQueueDashboard({
  onReviewTemplate,
  compact = false,
}: ApprovalQueueDashboardProps) {
  const { tenantId } = useCurrentTenant();
  const [filter, setFilter] = useState<ApprovalQueueFilter>({
    criticalityLevel: 'All',
    deadline: 'All',
    status: 'PendingReview',
  });
  const [searchQuery, setSearchQuery] = useState('');

  const {
    queue,
    totalPending,
    overdueCount,
    criticalCount,
    lifeCriticalCount,
    loading,
    error,
    refetch,
    getTimeRemaining,
    getApprovalProgressPercentage,
    requiresDualControl,
    getPendingApprovers,
  } = useApprovalQueue(tenantId, filter);

  // Filter queue by search query
  const filteredQueue = useMemo(() => {
    if (!searchQuery) return queue;

    const lowerQuery = searchQuery.toLowerCase();
    return queue.filter(
      (instance) =>
        instance.templateName.toLowerCase().includes(lowerQuery) ||
        instance.templateDomain.toLowerCase().includes(lowerQuery) ||
        instance.submitterName.toLowerCase().includes(lowerQuery)
    );
  }, [queue, searchQuery]);

  // Handle review button click
  const handleReview = (instanceId: string) => {
    if (onReviewTemplate) {
      onReviewTemplate(instanceId);
    }
  };

  // Get criticality badge color
  const getCriticalityColor = (criticality: string) => {
    switch (criticality) {
      case 'LifeCritical':
        return 'red';
      case 'Critical':
        return 'orange';
      case 'Operational':
        return 'blue';
      default:
        return 'gray';
    }
  };

  // Compact view
  if (compact) {
    return (
      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Group justify="space-between">
          <Group>
            <ThemeIcon
              color={overdueCount > 0 ? 'red' : 'blue'}
              variant="light"
              size="lg"
            >
              <IconShieldCheck size={20} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={500}>
                Pending Approvals
              </Text>
              <Text size="xs" c="dimmed">
                {totalPending} templates awaiting review
              </Text>
            </div>
          </Group>
          <Group>
            {overdueCount > 0 && <Badge color="red">{overdueCount} Overdue</Badge>}
            {lifeCriticalCount > 0 && (
              <Badge color="red" variant="light">
                {lifeCriticalCount} Life-Critical
              </Badge>
            )}
            <Button
              size="xs"
              variant="light"
              onClick={() => refetch()}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>
        </Group>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="red"
        title="Error Loading Approval Queue"
      >
        {error.message}
      </Alert>
    );
  }

  // Full dashboard view
  return (
    <Stack gap="md">
      {/* Header */}
      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Group>
            <ThemeIcon
              size="lg"
              color={overdueCount > 0 ? 'red' : 'blue'}
              variant="light"
            >
              <IconShieldCheck size={24} />
            </ThemeIcon>
            <div>
              <Title order={3}>Template Approval Queue</Title>
              <Text size="sm" c="dimmed">
                Dual-control approval workflow for life-critical AI templates
              </Text>
            </div>
          </Group>

          <Group>
            <ActionIcon
              variant="light"
              size="lg"
              onClick={() => refetch()}
              loading={loading}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Summary badges */}
        <Group>
          <Badge size="lg" color="blue" leftSection={<IconUser size={14} />}>
            {totalPending} Pending
          </Badge>
          {overdueCount > 0 && (
            <Badge
              size="lg"
              color="red"
              leftSection={<IconAlertTriangle size={14} />}
            >
              {overdueCount} Overdue
            </Badge>
          )}
          {lifeCriticalCount > 0 && (
            <Badge
              size="lg"
              color="red"
              variant="light"
              leftSection={<IconShieldCheck size={14} />}
            >
              {lifeCriticalCount} Life-Critical
            </Badge>
          )}
          {criticalCount > 0 && (
            <Badge size="lg" color="orange" variant="light">
              {criticalCount} Critical
            </Badge>
          )}
        </Group>
      </Card>

      {/* Filters */}
      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Group mb="xs">
          <ThemeIcon size="sm" variant="light">
            <IconFilter size={14} />
          </ThemeIcon>
          <Text fw={500}>Filters</Text>
        </Group>

        <Group grow>
          <TextInput
            placeholder="Search templates, domains, submitters..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <Select
            label="Criticality"
            data={['All', 'LifeCritical', 'Critical', 'Operational']}
            value={filter.criticalityLevel || 'All'}
            onChange={(value) =>
              setFilter({
                ...filter,
                criticalityLevel: value as ApprovalQueueFilter['criticalityLevel'],
              })
            }
          />

          <Select
            label="Deadline"
            data={['All', 'Urgent', 'Normal', 'Routine']}
            value={filter.deadline || 'All'}
            onChange={(value) =>
              setFilter({
                ...filter,
                deadline: value as ApprovalQueueFilter['deadline'],
              })
            }
          />
        </Group>
      </Card>

      {/* Approval queue */}
      {loading && !queue.length ? (
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Group justify="center">
            <Loader />
            <Text c="dimmed">Loading approval queue...</Text>
          </Group>
        </Card>
      ) : filteredQueue.length === 0 ? (
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Group justify="center">
            <ThemeIcon size="xl" variant="light" color="green">
              <IconCheck size={30} />
            </ThemeIcon>
            <div>
              <Text fw={500}>All caught up!</Text>
              <Text size="sm" c="dimmed">
                No templates pending approval
              </Text>
            </div>
          </Group>
        </Card>
      ) : (
        <Stack gap="sm">
          {filteredQueue.map((instance) => (
            <ApprovalQueueCard
              key={instance.instanceId}
              instance={instance}
              onReview={handleReview}
              getTimeRemaining={getTimeRemaining}
              getApprovalProgressPercentage={getApprovalProgressPercentage}
              requiresDualControl={requiresDualControl}
              getPendingApprovers={getPendingApprovers}
              getCriticalityColor={getCriticalityColor}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Approval Queue Card Component
 *
 * Individual approval instance card with:
 * - Criticality badge
 * - Deadline countdown
 * - Approval progress
 * - Approver avatars
 * - Review button
 */
interface ApprovalQueueCardProps {
  instance: ApprovalWorkflowInstance;
  onReview: (instanceId: string) => void;
  getTimeRemaining: (instance: ApprovalWorkflowInstance) => {
    isOverdue: boolean;
    hours: number;
    minutes: number;
    label: string;
    color: string;
  };
  getApprovalProgressPercentage: (instance: ApprovalWorkflowInstance) => number;
  requiresDualControl: (instance: ApprovalWorkflowInstance) => boolean;
  getPendingApprovers: (instance: ApprovalWorkflowInstance) => any[];
  getCriticalityColor: (criticality: string) => string;
}

function ApprovalQueueCard({
  instance,
  onReview,
  getTimeRemaining,
  getApprovalProgressPercentage,
  requiresDualControl,
  getPendingApprovers,
  getCriticalityColor,
}: ApprovalQueueCardProps) {
  const timeRemaining = getTimeRemaining(instance);
  const progressPercentage = getApprovalProgressPercentage(instance);
  const isDualControl = requiresDualControl(instance);
  const pendingApprovers = getPendingApprovers(instance);

  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      style={{
        borderColor:
          timeRemaining.isOverdue ? 'var(--mantine-color-red-6)' :
          timeRemaining.color === 'orange' ? 'var(--mantine-color-orange-6)' :
          undefined,
        borderWidth: timeRemaining.isOverdue ? 2 : 1,
      }}
    >
      <Stack gap="sm">
        {/* Header row */}
        <Group justify="space-between" ta="left">
          <div style={{ flex: 1 }}>
            <Group mb="xs">
              <Badge
                size="md"
                color={getCriticalityColor(instance.criticalityLevel)}
                leftSection={
                  instance.criticalityLevel === 'LifeCritical' ? (
                    <IconAlertTriangle size={14} />
                  ) : null
                }
              >
                {instance.criticalityLevel}
              </Badge>

              {isDualControl && (
                <Badge size="sm" color="red" variant="outline">
                  Dual Control Required
                </Badge>
              )}

              {instance.validationSummary.errorCount > 0 && (
                <Badge size="sm" color="orange" variant="outline">
                  {instance.validationSummary.errorCount} Validation Errors
                </Badge>
              )}
            </Group>

            <Title order={5}>{instance.templateName}</Title>
            <Text size="sm" c="dimmed" mt={4}>
              {instance.templateDomain} • Submitted by {instance.submitterName}
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              {new Date(instance.submittedAt).toLocaleString()}
            </Text>
          </div>

          <Button
            size="sm"
            onClick={() => onReview(instance.instanceId)}
            leftSection={<IconShieldCheck size={16} />}
          >
            Review
          </Button>
        </Group>

        {/* Deadline countdown */}
        <Paper withBorder p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
          <Group justify="space-between">
            <Group gap="xs">
              <IconClock
                size={18}
                color={
                  timeRemaining.isOverdue ? 'var(--mantine-color-red-6)' :
                  timeRemaining.color === 'orange' ? 'var(--mantine-color-orange-6)' :
                  'var(--mantine-color-gray-6)'
                }
              />
              <div>
                <Text
                  size="sm"
                  fw={500}
                  c={
                    timeRemaining.isOverdue ? 'red' :
                    timeRemaining.color === 'orange' ? 'orange' :
                    'dimmed'
                  }
                >
                  {timeRemaining.label}
                </Text>
                <Text size="xs" c="dimmed">
                  Deadline: {new Date(instance.deadline).toLocaleString()}
                </Text>
              </div>
            </Group>

            {timeRemaining.isOverdue && (
              <Badge color="red" size="sm">
                ESCALATED
              </Badge>
            )}
          </Group>
        </Paper>

        {/* Approval progress */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={500}>
              Approval Progress
            </Text>
            <Text size="sm" c="dimmed">
              {instance.approvalProgress.completed} / {instance.approvalProgress.required}{' '}
              approvals
            </Text>
          </Group>

          <Progress
            value={progressPercentage}
            color={
              instance.criticalityLevel === 'LifeCritical' ? 'red' :
              instance.criticalityLevel === 'Critical' ? 'orange' :
              'blue'
            }
            size="md"
            animated={progressPercentage > 0 && progressPercentage < 100}
          />
        </div>

        {/* Required approvers */}
        <div>
          <Text size="sm" fw={500} mb="xs">
            Required Approvers
          </Text>
          <Group gap="xs">
            {instance.requiredApprovers.map((approver) => (
              <Tooltip
                key={approver.userId}
                label={
                  <div>
                    <Text size="sm" fw={500}>
                      {approver.name}
                    </Text>
                    <Text size="xs">{approver.role}</Text>
                    {approver.expertise.length > 0 && (
                      <Text size="xs" c="dimmed">
                        Expertise: {approver.expertise.join(', ')}
                      </Text>
                    )}
                    {approver.hasApproved && approver.approvedAt && (
                      <Text size="xs" c="green">
                        Approved: {new Date(approver.approvedAt).toLocaleString()}
                      </Text>
                    )}
                  </div>
                }
                withArrow
              >
                <Avatar
                  size="md"
                  color={approver.hasApproved ? 'green' : 'gray'}
                  style={{
                    border: approver.hasApproved
                      ? '2px solid var(--mantine-color-green-6)'
                      : '1px solid var(--mantine-color-gray-4)',
                  }}
                >
                  {approver.hasApproved ? (
                    <IconCheck size={16} />
                  ) : (
                    approver.name.charAt(0).toUpperCase()
                  )}
                </Avatar>
              </Tooltip>
            ))}
          </Group>

          {pendingApprovers.length > 0 && (
            <Text size="xs" c="dimmed" mt="xs">
              Waiting for: {pendingApprovers.map((a) => a.name).join(', ')}
            </Text>
          )}
        </div>

        {/* Life-critical warning */}
        {instance.criticalityLevel === 'LifeCritical' && (
          <Alert
            color="red"
            icon={<IconAlertTriangle size={16} />}
            title="Life-Critical Template"
          >
            <Text size="xs">
              This template requires {instance.approvalProgress.required} independent approvals
              due to life-critical risk classification (HIPAA/FDA compliance).
            </Text>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
